import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { clientSession, Content, fetchContentById, KIDS_CATEGORY_URL } from 'common/apiService';
import { useUser } from 'common/CacheProvider';
import ShortsContainer from 'common/Components/Shorts/ShortsContainer';
import { Restriction } from 'common/Components/Shorts/RestrictionNotice';
import { flattenEpisodes, getEpisodeUrl, isLocked, resolveInitialFileId } from 'common/Components/Shorts/shortEpisodes';
import useShortSource from 'common/Components/Shorts/useShortSource';
import Spinner, { WrapperSpinner } from 'common/styles/Spinner';
import { inspectFlags } from 'common/utils';
import useConditionalProps from 'common/utils/useConditionalProps';
import lscache from 'lscache';

/** The one refusal that has its own screen here rather than a redirect out of the short. */
const isAgeRestricted = (err: any) => (
  err?.response?.status === 451 &&
  err?.response?.data?.meta?.operation_result === 'content_is_age_restricted'
);

export default function ShortPage() {
  const router = useRouter();
  const [shortId, , fileId] = (router.query.params as string[]) || [];
  const { authorized, hasSubscription, loaded } = useUser();

  const fetchProps = useConditionalProps((s) => s.fetch);
  const setPublicProps = useConditionalProps((s) => s.setPublicProps);
  const ageRestricted = useConditionalProps((s) => s.ageRestricted);

  const [content, setContent] = useState<Content>();
  // Set when a request — rather than the short's own flags — is what reveals the restriction.
  const [refusal, setRefusal] = useState<Restriction>();

  // Only for what leaves nothing to render. A 402 reaches this from the short's own request,
  // never from an episode's: a paid episode is answered with a notice over the player.
  const handleApiError = (err: any) => {
    const { status } = err?.response || {};
    if (status === 402) router.push(`/packages?redirect=${router.asPath}`);
    else if (status === 423 || status === 500 || status === 404) router.push('/');
  };

  // Keyed on the id alone: an episode change rewrites the URL, and refetching the whole short
  // for that would tear the player down for nothing.
  useEffect(() => {
    if (!shortId) return;
    // A short reached by its long id (a poster links that way) is rewritten to its short id once
    // it lands; that is the same short, so it isn't fetched a second time.
    if (content && (content.short_id === shortId || content.id === shortId)) return;
    let cancelled = false;

    // Drop the previous short first: until the new one lands, its episodes would be matched
    // against an id from the URL that belongs to a different short.
    setContent(undefined);

    fetchContentById(shortId, clientSession)
      .then(({ data }) => {
        if (!cancelled) setContent(data);
      })
      .catch((err) => {
        if (cancelled) return;
        // The global handler stands down on this route so the notice can be shown over the
        // player — but with the short itself withheld there is nothing to show it over.
        if (isAgeRestricted(err)) router.replace(KIDS_CATEGORY_URL);
        else handleApiError(err);
      });

    return () => {
      cancelled = true;
      lscache.set('player_config', { ...lscache.get('player_config'), tech: 'hls' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortId]);

  useEffect(() => {
    if (!content) return;
    setPublicProps({
      contentId: content.short_id,
      contentLongId: content.id,
      flags: [content.conditional_flag, content.flag].join(', '),
      ticket_info: content.ticket_info,
    });
    if (authorized) fetchProps();
  }, [content, authorized, setPublicProps, fetchProps]);

  const episodes = useMemo(() => flattenEpisodes(content), [content]);
  const activeIndex = episodes.findIndex((episode) => episode.file_id === fileId);
  const activeEpisode = episodes[activeIndex];

  // The episode is part of the path, so a URL without one — or naming an episode this short
  // doesn't have — is rewritten to where playback should start.
  useEffect(() => {
    if (!content || activeEpisode) return;
    const initialFileId = resolveInitialFileId(content) || episodes[0]?.file_id;
    if (initialFileId) router.replace(getEpisodeUrl(content, initialFileId), undefined, { shallow: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, activeEpisode, episodes]);

  const prevFileId = activeIndex > 0 ? episodes[activeIndex - 1]?.file_id : undefined;
  const nextFileId = activeIndex >= 0 ? episodes[activeIndex + 1]?.file_id : undefined;
  // Ordered ids let the container tell which way a jump from the episode grid is travelling.
  const episodeIds = useMemo(() => episodes.map(({ file_id }) => file_id || ''), [episodes]);

  // A restricted episode is told so over the player: there is no content page to send the
  // viewer to, which is what the /play route does for everything else. The paywall comes last —
  // it is the only one of the three they can act on, so a harder wall is worth saying first.
  const { isForeignIP } = inspectFlags([content?.conditional_flag, content?.flag].join(', '));
  // Held until the cached user has hydrated, so a subscriber never meets the paywall for a
  // frame on the way in.
  const paywalled = loaded && isLocked(activeEpisode, authorized && hasSubscription);
  const restriction: Restriction | undefined = ageRestricted ? 'age'
    : isForeignIP ? 'ip'
      : paywalled ? 'subscription'
        : refusal;

  // Nothing is requested for an episode the viewer can't watch: the notice stands in for the
  // player, so there is no source to load behind it.
  const { tech, ready, error, retry } = useShortSource({
    content,
    fileId: activeEpisode?.file_id,
    nextFileId,
    skip: !!restriction,
  });

  // A refusal is the episode's, not the short's: the next one is asked about on its own.
  useEffect(() => setRefusal(undefined), [fileId]);

  useEffect(() => {
    if (!error) return;
    // The file endpoint is the last place a restriction can surface, and by then the short is
    // loaded — so the notice has somewhere to sit and none of these need a redirect.
    if (isAgeRestricted(error)) setRefusal('age');
    else if (error?.response?.status === 402) setRefusal('subscription');
    else handleApiError(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const selectEpisode = useCallback(
    (nextFileId?: string) => {
      if (!content || !nextFileId || nextFileId === fileId) return;
      // replace, not push: swiping shouldn't leave a history entry per episode passed through.
      router.replace(getEpisodeUrl(content, nextFileId), undefined, { shallow: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content, fileId],
  );

  if (!content) {
    return (
      <WrapperSpinner>
        <Spinner block size="big" color="primary" />
      </WrapperSpinner>
    );
  }

  return (
    <>
      {/* Shorts are a player surface, not an indexable page — same treatment as /play. */}
      <Head>
        <meta name="robots" content="noindex, follow" />
      </Head>
      <ShortsContainer
        content={content}
        activeFileId={activeEpisode?.file_id}
        prevFileId={prevFileId}
        nextFileId={nextFileId}
        episodeIds={episodeIds}
        restriction={restriction}
        onSelectEpisode={selectEpisode}
        stage={{ tech, ready, failed: !!error && !restriction, onRetry: retry }}
      />
    </>
  );
}
